import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { ColDef, GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
  Box,
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  isCompanySettingsReadOnly,
  VStack,
} from "../../../components/utils";
import { CTADropdown } from "../../../components/shared/Dropdown";
import {
  useGetRepricingHistory,
  useApproveOptionsRepricing,
  useUpdateOptionsRepricingSummaryVisibility,
} from "../../../queries/onBoarding";
import { formatDisplayDate } from "../../../utils/date";
import {
  formatCurrency,
  getCurrencyType,
} from "../../../utils/currencyFormatter";
import StatusLabel from "../../esopOverview/StatusLabel";

export default function RepricingHistoryPage() {
  const isUserReadOnly = isCompanySettingsReadOnly();
  const navigate = useNavigate();
  const currencyType = getCurrencyType();
  const gridApi = useRef<GridApi | null>(null);
  const { data: repricingEvents = [], isLoading } = useGetRepricingHistory();
  const { mutate: approveRepricing } = useApproveOptionsRepricing();
  const {
    mutate: updateSummaryVisibility,
    isLoading: isUpdatingSummaryVisibility,
  } = useUpdateOptionsRepricingSummaryVisibility();
  const [pendingSummaryVisibility, setPendingSummaryVisibility] = useState<{
    id: string;
    enabled: boolean;
  } | null>(null);

  function handleApprove(id: string) {
    approveRepricing(id, {
      onSuccess: () => {
        toast.success("Repricing approved successfully");
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.errorRaw || "Error approving repricing"
        );
      },
    });
  }

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      flex: 1,
      resizable: true,
      wrapText: true,
      autoHeight: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: "multiline",
      filter: true,
      menuTabs: [],
      filterParams: { buttons: [] },
      minWidth: 120,
    }),
    []
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "NAME",
        field: "name",
        minWidth: 180,
      },
      {
        headerName: "EFFECTIVE DATE",
        field: "date",
        minWidth: 140,
        valueFormatter: (p) =>
          p.value ? formatDisplayDate(String(p.value)) : "—",
      },
      {
        headerName: "TYPE",
        field: "repricingType",
        minWidth: 130,
        cellRenderer: (params: any) => {
          const type = params.value as "GRANT" | "VESTING" | undefined;
          if (!type) return "—";
          return (
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                type === "GRANT"
                  ? "bg-blue-50 text-blue-700"
                  : "bg-purple-50 text-purple-700"
              }`}
            >
              {type === "GRANT" ? "Grant Level" : "Vesting Level"}
            </span>
          );
        },
      },
      {
        headerName: "NEW STRIKE PRICE",
        field: "newStrikePrice",
        minWidth: 160,
        valueFormatter: (p) =>
          p.value != null
            ? formatCurrency(Number(p.value), currencyType || "")
            : "—",
      },
      {
        headerName: "STATUS",
        field: "state",
        minWidth: 120,
        cellRenderer: (params: any) => (
          <StatusLabel state={params.value || "DRAFT"} />
        ),
      },
      {
        headerName: "",
        field: "actions",
        pinned: "right",
        hide: false,
        width: 80,
        maxWidth: 80,
        filter: false,
        colId: "action-column",
        resizable: false,
        sortable: false,
        menuTabs: [],
        cellRenderer: CTADropdown,
        cellRendererParams: (params: any) => ({
          actions:
            params.data?.state === "DRAFT"
              ? [
                  { name: "Edit", disabled: isUserReadOnly },
                  { name: "Approve", disabled: isUserReadOnly },
                ]
              : [
                  { name: "View" },
                  {
                    name: params.data?.showOptionsRepricingSummary
                      ? "Hide employee summary"
                      : "Show employee summary",
                    disabled: isUserReadOnly,
                  },
                ],
          onAction: (action: { name: string }) =>
            handleAction(params.data, action),
        }),
      },
    ],
    [currencyType, isUserReadOnly, navigate]
  );

  function handleAction(
    row: any,
    action: { name: string; disabled?: boolean }
  ) {
    if (action.disabled) return;
    if (action.name === "Edit") {
      navigate(`/options/optionsRepricing/edit/${row.id}`, {
        state: { record: row },
      });
    } else if (action.name === "View") {
      navigate(`/options/optionsRepricing/edit/${row.id}`, {
        state: { record: row, readonly: true },
      });
    } else if (action.name === "Approve") {
      handleApprove(row.id);
    } else if (
      action.name === "Show employee summary" ||
      action.name === "Hide employee summary"
    ) {
      setPendingSummaryVisibility({
        id: row.id,
        enabled: !row.showOptionsRepricingSummary,
      });
    }
  }

  function confirmSummaryVisibility() {
    if (!pendingSummaryVisibility) return;
    updateSummaryVisibility(
      {
        corporateActionId: pendingSummaryVisibility.id,
        showOptionsRepricingSummary: pendingSummaryVisibility.enabled,
      },
      {
        onSuccess: () => {
          toast.success("Employee portal summary setting saved");
          setPendingSummaryVisibility(null);
        },
        onError: (err: any) => {
          toast.error(
            err?.response?.data?.errorRaw ||
              "Unable to save employee portal summary setting"
          );
        },
      }
    );
  }

  return (
    <VStack className="w-full gap-4 p-6">
      <HStack className="justify-between items-center">
        <VStack className="gap-0.5">
          <p className="text-base font-semibold text-gray-800">
            Options Repricing
          </p>
          <p className="text-sm text-gray-500">
            {repricingEvents.length} repricing event
            {repricingEvents.length !== 1 ? "s" : ""}
          </p>
        </VStack>
        <ButtonPrimary
          onClick={() => navigate("/options/optionsRepricing/new")}
          type="button"
          disabled={isUserReadOnly}
        >
          <Icon icon="mdi:plus" width={16} className="mr-1" />
          New Repricing
        </ButtonPrimary>
      </HStack>

      {isLoading ? (
        <VStack className="py-20 items-center gap-2">
          <Icon
            icon="lucide:loader-2"
            className="animate-spin text-gray-400"
            width={32}
          />
          <p className="text-sm text-gray-400">Loading...</p>
        </VStack>
      ) : (
        <Box style={{ height: "400px" }} className="w-full ag-theme-material">
          <AgGridReact
            rowData={repricingEvents.map((event) => ({
              ...event,
              newStrikePrice:
                event.metaData?.repricingDetails?.newStrikePrice != null
                  ? Number(event.metaData.repricingDetails.newStrikePrice)
                  : null,
              repricingType:
                event.metaData?.repricingDetails?.repricingType || null,
              showOptionsRepricingSummary:
                event.metaData?.repricingDetails?.showOptionsRepricingSummary ??
                false,
            }))}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={(params) => {
              gridApi.current = params.api;
            }}
            animateRows
            suppressRowTransform
            pagination
            rowClass="border-t border-dashed cursor-pointer hover:bg-slate-50"
            onRowClicked={(params) => {
              if (!isUserReadOnly) {
                handleAction(params.data, {
                  name: params.data?.state === "DRAFT" ? "Edit" : "View",
                });
              } else {
                handleAction(params.data, { name: "View" });
              }
            }}
            overlayNoRowsTemplate='<span style="padding:10px">No repricing events found</span>'
          />
        </Box>
      )}
      <Dialog
        open={pendingSummaryVisibility !== null}
        onClose={
          isUpdatingSummaryVisibility
            ? undefined
            : () => setPendingSummaryVisibility(null)
        }
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm employee summary visibility</DialogTitle>
        <DialogContent>
          <p className="text-sm text-gray-600">
            {pendingSummaryVisibility?.enabled
              ? "Show this repricing summary to employees whose options were repriced?"
              : "Hide this repricing summary from employees?"}
          </p>
        </DialogContent>
        <DialogActions>
          <ButtonSecondary
            type="button"
            onClick={() => setPendingSummaryVisibility(null)}
            disabled={isUpdatingSummaryVisibility}
          >
            Cancel
          </ButtonSecondary>
          <ButtonPrimary
            type="button"
            onClick={confirmSummaryVisibility}
            loading={isUpdatingSummaryVisibility}
            disabled={isUpdatingSummaryVisibility}
          >
            {pendingSummaryVisibility?.enabled
              ? "Show summary"
              : "Hide summary"}
          </ButtonPrimary>
        </DialogActions>
      </Dialog>
    </VStack>
  );
}
